import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { SelectionModel } from '@angular/cdk/collections';
import { AllocationBandStat, AllocationResult, UserAllocationBreakdown } from '../Models/UserAllocationBreakdown';
import { UserService } from '../services/user.service';

@Component({
  selector: 'app-allocation',
  templateUrl: './allocation.component.html',
  styleUrls: ['./allocation.component.css'],
  standalone: false
})
export class AllocationComponent implements OnInit {
  breakdown: UserAllocationBreakdown[] = [];
  selection = new SelectionModel<UserAllocationBreakdown>(true, []);

  loading = false;
  allocating = false;

  private loggedInUserTag = '';

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    const user = this.userService.loggedInUser;
    this.loggedInUserTag = `${user?.lastName}, ${user?.firstName}`;
    this.loadBreakdown();
  }

  async loadBreakdown(): Promise<void> {
    this.loading = true;
    try {
      const params = new HttpParams().set('user', this.loggedInUserTag);
      const result = await lastValueFrom(
        this.http.get<UserAllocationBreakdown[]>('/charitycentral/GetAllocationBreakdown', { params })
      );
      this.breakdown = result ?? [];
      // Row objects are freshly deserialized on every load, so any prior selection (matched by
      // reference) can no longer match - clear it rather than leave silently-orphaned entries.
      this.selection.clear();
    } catch {
      this.toastr.error('Failed to load allocation breakdown.', '', { timeOut: 4000 });
    } finally {
      this.loading = false;
    }
  }

  isAllSelected(): boolean {
    return this.breakdown.length > 0 && this.breakdown.every(row => this.selection.isSelected(row));
  }

  isSomeSelected(): boolean {
    return !this.isAllSelected() && this.breakdown.some(row => this.selection.isSelected(row));
  }

  masterToggle(): void {
    if (this.isAllSelected()) {
      this.breakdown.forEach(row => this.selection.deselect(row));
    } else {
      this.selection.clear();
      this.breakdown.forEach(row => this.selection.select(row));
    }
  }

  // "5 / 10" when a target is configured for this band, otherwise just the bare current count.
  formatBand(stat: AllocationBandStat): string {
    return stat.target != null ? `${stat.current} / ${stat.target}` : `${stat.current}`;
  }

  async allocate(): Promise<void> {
    if (this.selection.selected.length === 0) return;

    this.allocating = true;
    try {
      let params = new HttpParams().set('user', this.loggedInUserTag);
      for (const row of this.selection.selected) {
        params = params.append('userIds', row.userId);
      }

      const results = await lastValueFrom(
        this.http.post<AllocationResult[]>('/charitycentral/ResetAllocations', null, { params })
      );

      const totalAssigned = (results ?? []).reduce((sum, r) => sum + r.assignedCount, 0);
      const userCount = results?.length ?? 0;
      this.toastr.success(
        `Assigned ${totalAssigned} probate${totalAssigned === 1 ? '' : 's'} across ${userCount} user${userCount === 1 ? '' : 's'}.`,
        '', { timeOut: 4000 }
      );

      await this.loadBreakdown();
    } catch {
      this.toastr.error('Failed to allocate probates.', '', { timeOut: 4000 });
    } finally {
      this.allocating = false;
    }
  }
}
